// Overview insight — the two Dashboard panels docs/REBUILD_PARITY.md lists as
// missing ("Weekly hours chart", "Top jobs this week").
//
// THE CONTRACT, read off server/routes/dashboard.js (2026-09-03). Nothing here
// is inferred from a field name; every name below is in that file.
//
//   GET /api/dashboard/weekly-chart[?weeks=N]      authMiddleware only
//     ?weeks  Math.max(1, parseInt(weeks) || 1) — N*7 days ending with the
//             current week. We ask for the default, one week.
//     -> ARRAY of { date: 'YYYY-MM-DD', day: 'Mon', hours: <decimal hours,
//                   parseFloat(x.toFixed(2))>, entries: <count> }
//       Always 7*N elements: missing days come back as hours 0, entries 0.
//       No money in it.
//
//   GET /api/dashboard/top-jobs                    authMiddleware only
//     No query parameters at all — LIMIT 5 is hardcoded in the SQL.
//     -> ARRAY of { id, title, status, week_seconds: <RAW SECONDS, not hours>,
//                   session_count, worker_name }  (worker_name is jobs.user_id,
//       i.e. the lead). NO money field. Nothing here is a dollar figure, so
//       nothing here is money math (CLAUDE.md rule 4): week_seconds / 3600 is a
//       unit change on a duration, the same one records/OfficePage.js hoursOf()
//       already does.
//
//   GATING: both are role-scoped, not permission-scoped. `financials` is not
//   consulted by either handler. `isAdminOrManager = role === 'admin' ||
//   role === 'manager'` widens the query to the whole company; every other
//   role — including `accounting`, the role Office was built for — gets
//   `AND te.user_id = ?` with their own id. So a bookkeeper's chart is their
//   OWN time entries, which is a week of zeros that reads as a fact about the
//   company. insightScope() below says so on the screen rather than letting
//   the zeros speak; Overview.test.js reads dashboard.js and fails if that
//   predicate changes.
//
// Why here and not inside Overview.js: Overview is a stat grid over one query
// and stays readable at ~90 lines. These are two more queries, a chart and a
// table; folding them in would triple it. `insight/` is already where Office
// keeps its analytic surfaces (Reports.js).

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SectionLabel, Notice, Skeleton, DataTable, Pill, Stat, formatHours } from '../../../ui/fw';
import { get, describeError } from '../../shared/http';
import { useRole } from '../../../hooks/usePermission';
import { jobBadges } from '../../shared/jobFields';
import { fmtDateRange, fmtMoney } from '../shared/format';
import './OverviewInsight.css';

const DASH = '—';

/**
 * Which rows the server will have counted for this role.
 *
 * Duplicated from server/routes/dashboard.js on purpose and checked against it
 * by a test, per CLAUDE.md "declare a contract once, and check it against the
 * server" — the alternative is a caption that quietly becomes false.
 */
// accounting joined this list on 2026-09-03, when the two handlers were
// aligned with /stats — which had always counted accounting as a company-wide
// personnel view. Before that a bookkeeper's chart was seven of their own zero
// hours, captioned as their own, which was honest but useless.
export const COMPANY_WIDE_ROLES = ['admin', 'manager', 'accounting'];
export function insightScope(role) {
  return COMPANY_WIDE_ROLES.includes(role) ? 'company' : 'self';
}

/**
 * Decimal hours the server sent -> '8.25'. `DASH` when there is no number.
 *
 * The guard is the point. ui/fw formatHours() renders 0.00 for anything
 * unreadable — safe in a payroll column where the server always sends a
 * number, a lie here where a missing field must not become a zero.
 */
export function hoursText(hours) {
  if (hours === null || hours === undefined || hours === '') return DASH;
  const n = Number(hours);
  return Number.isFinite(n) ? formatHours(n) : DASH;
}

/** RAW SECONDS the server sent (top-jobs week_seconds) -> '8.25' hours. */
export function secondsHoursText(seconds) {
  if (seconds === null || seconds === undefined || seconds === '') return DASH;
  const n = Number(seconds);
  return Number.isFinite(n) ? formatHours(n / 3600) : DASH;
}

/** Bar width as a whole percent of the week's peak day. Geometry, not a figure. */
export function barPct(hours, maxHours) {
  const v = Number(hours);
  const max = Number(maxHours);
  if (!Number.isFinite(v) || v <= 0 || !Number.isFinite(max) || max <= 0) return 0;
  return Math.min(100, Math.round((v / max) * 100));
}

/** The peak day's hours — the bar scale only. 0 when no day has any. */
export function peakHours(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((m, r) => {
    const v = Number(r && r.hours);
    return Number.isFinite(v) && v > m ? v : m;
  }, 0);
}

function Panel({ name, title, meta, action, children }) {
  return (
    <section className="of-card" data-panel={name}>
      <div className="of-card__head">
        <SectionLabel>{title}</SectionLabel>
        {action || null}
      </div>
      {meta ? <p className="of-muted" data-panel-meta={name}>{meta}</p> : null}
      {children}
    </section>
  );
}

/**
 * Hours per day for the current week, as horizontal CSS bars.
 *
 * Every value is also rendered as text in its own row, so the panel is
 * readable without seeing the bars at all — a bar chart whose numbers are only
 * bar heights is decoration. The track carries aria-hidden, so a screen reader
 * reads each row as "Mon 8.25". Horizontal rather than vertical because seven
 * vertical bars with their values above them stop being legible at 390px, and
 * the row grid has min-width:0 on the only flexible column so nothing can push
 * the page sideways.
 */
export function WeeklyHoursPanel() {
  const q = useQuery({ queryKey: ['office', 'overview', 'weekly-chart'], queryFn: () => get('/dashboard/weekly-chart') });

  // NOT `q.data || []`. An empty array is an answer ("no hours this week") and
  // a refusal is not; collapsing the two is the defect docs/AGENT_LEARNINGS.md
  // 2026-09-03 found eight times in this directory.
  const rows = Array.isArray(q.data) ? q.data : null;
  const max = peakHours(rows);
  const span = rows && rows.length ? fmtDateRange(rows[0].date, rows[rows.length - 1].date) : null;

  return (
    <Panel
      name="weekly-hours"
      title="Hours this week"
      meta={span}
      action={<Link className="of-link" to="/reports">Full report</Link>}
    >
      {q.isPending ? <Skeleton lines={4} /> : null}
      {q.error ? <Notice tone="stop" data-panel-error="weekly-hours">{describeError(q.error, 'Could not load the weekly hours.')}</Notice> : null}
      {rows ? (
        <>
          {rows.length > 0 ? (
            <ol className="ofi-chart">
              {rows.map((r, i) => {
                const pct = barPct(r.hours, max);
                return (
                  <li
                    className="ofi-chart__row"
                    key={r.date || i}
                    data-day={r.day}
                    data-peak={max > 0 && Number(r.hours) === max ? 'true' : undefined}
                  >
                    <span className="ofi-chart__day">{r.day || r.date || DASH}</span>
                    <span className="ofi-chart__track" aria-hidden="true">
                      <span className="ofi-chart__bar" data-empty={pct === 0 ? 'true' : undefined} style={{ width: `${pct}%` }} />
                    </span>
                    <span className="ofi-chart__value fw-num">{hoursText(r.hours)}</span>
                  </li>
                );
              })}
            </ol>
          ) : null}
          {max === 0 ? <p className="of-muted">No hours were tracked this week.</p> : null}
        </>
      ) : null}
    </Panel>
  );
}

/** The five jobs that took the most time this week, in the server's own order. */
export function TopJobsPanel() {
  const q = useQuery({ queryKey: ['office', 'overview', 'top-jobs'], queryFn: () => get('/dashboard/top-jobs') });

  const rows = Array.isArray(q.data) ? q.data : null;
  // A count only once a count exists. "0 jobs" over a refused request reads as
  // arithmetic, and arithmetic is trusted (AGENT_LEARNINGS 2026-08-17).
  const meta = rows ? `${rows.length} ${rows.length === 1 ? 'job' : 'jobs'} with hours this week` : null;

  // As a card, in the server's own order: the job is the headline (it already
  // carries the lead's name under it), the status is the badge, and the two
  // numbers are labelled facts.
  //
  // The rank column is `hidden` — card-only, the table keeps it. A stack of
  // cards IS the ranking, top to bottom, and "# 1" as a fact would spend one
  // of three fact slots restating the order the reader can already see.
  //
  // The columns and their order are unchanged: office/screens/Overview.test.js
  // reads Sessions and Hours out of this table POSITIONALLY (cells[3],
  // cells[4]), so adding or removing one here breaks a suite this file does
  // not own. Roles are presentation hints the table ignores, which is why the
  // card could be fixed without touching the row.
  const columns = [
    { key: 'rank', label: '#', align: 'right', width: '2.5rem', role: 'hidden', render: (r, i) => <span className="ofi-rank fw-num">{i + 1}</span> },
    {
      key: 'title',
      label: 'Job',
      role: 'title',
      render: (r) => (
        <>
          {r.title || 'Untitled job'}
          <span className="of-cell__sub">{r.worker_name || 'No lead assigned'}</span>
        </>
      ),
    },
    { key: 'status', label: 'Status', role: 'badge', render: (r) => jobBadges(r).map((b) => <Pill key={b.key} tone={b.tone}>{b.label}</Pill>) },
    { key: 'session_count', label: 'Sessions', align: 'right', role: 'fact', render: (r) => (r.session_count === null || r.session_count === undefined ? DASH : r.session_count) },
    { key: 'week_seconds', label: 'Hours', align: 'right', role: 'fact', render: (r) => secondsHoursText(r.week_seconds) },
  ];

  return (
    <Panel name="top-jobs" title="Top jobs this week" meta={meta}>
      {q.isPending ? <Skeleton lines={4} /> : null}
      {q.error ? <Notice tone="stop" data-panel-error="top-jobs">{describeError(q.error, 'Could not load the top jobs.')}</Notice> : null}
      {rows ? (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r, i) => r.id ?? i}
          empty="No time was tracked against a job this week."
          caption="Jobs by hours this week"
          dense
        />
      ) : null}
    </Panel>
  );
}

/**
 * Payroll and billing, year to date — the classic dashboard's FinancialOverview.
 *
 * docs/PARITY_2026-09-08.md §11. `GET /api/dashboard/stats` has always built
 * this whole block and returned it as `financials`
 * (server/routes/dashboard.js:189), and until now a grep for `financials`
 * under client/src found only permission-gating prose. The payroll half —
 * gross, deductions, net, per worker, year to date — existed nowhere in the
 * app, and it is a question asked monthly.
 *
 * ── EVERY FIGURE IS DRAWN, NONE IS COMPUTED ────────────────────────────────
 *
 * CLAUDE.md rule 4. Nothing here adds, subtracts or scales a money value —
 * not even gross minus deductions, which the server already sends as
 * `total_paid`. `fmtMoney` and `hoursText` are formatters, and both render a
 * missing value as a dash rather than a zero.
 *
 * ── THE CONTRACT, read off server/routes/dashboard.js (2026-09-09) ─────────
 *
 * Confirmed against a scratch server, not inferred from field names:
 *
 *   financials: null                       role has no personnel view at all
 *   financials.year                        new Date().getFullYear() server-side
 *   financials.payroll                     admin / manager / accounting
 *     total_gross total_deductions total_paid total_hours paystub_count
 *     pending_amount pending_count         all `paid`/`pending` paystubs with
 *                                          pay_period_start >= Jan 1
 *     worker_breakdown[]                   ONLY with workers.read_sensitive —
 *       { name, total_paid, total_hours }  omitted for a plain manager
 *   financials.invoices                    ONLY with financials.read
 *     total_billed total_paid total_pending count
 *   financials.profit_estimate             ONLY with financials.read
 *   financials.tax_notes                   ONLY with financials.read
 *     estimated_income_tax estimated_se_tax note
 *
 * Each optional block is either drawn or NAMED AS WITHHELD. An absent
 * `worker_breakdown` rendered as an empty list would read as "no worker was
 * paid this year"; an absent `invoices` rendered through fmtMoney would put a
 * dash in a "Billed" slot beside real payroll figures, which reads as "we
 * billed nothing". Both are statements about the business made from no data.
 *
 * ── The one figure this panel refuses to draw ──────────────────────────────
 *
 * `tax_notes.estimated_income_tax` is `profit_estimate * 0.25` with no sign
 * guard, while its sibling `estimated_se_tax` in the same object IS guarded
 * `profit_estimate > 0` — so on a year that is showing a loss the server's own
 * two fields disagree. Measured on a scratch server with no invoices and
 * $3,570 of paystubs: `profit_estimate: -3570`,
 * `estimated_income_tax: -892.5`.
 *
 * "Estimated income tax -$892.50" on an owner's dashboard is exactly the
 * plausible-looking wrong figure CLAUDE.md calls the worst outcome available
 * here. The server's arithmetic is not touched — that would be money math and
 * needs a human — so the panel shows the loss, which is the real answer, and
 * says no tax is estimated on it.
 */
export function YearToDatePanel() {
  const q = useQuery({ queryKey: ['office', 'overview', 'ytd-financials'], queryFn: () => get('/dashboard/stats') });

  const fin = q.data && typeof q.data === 'object' && q.data.financials && typeof q.data.financials === 'object'
    ? q.data.financials
    : null;

  // Nothing at all for a role the server gave no financials to — and nothing
  // while the request is still in flight, so a worker's dashboard never
  // flashes a panel headed "Payroll" that then vanishes. An error still
  // speaks, because a refusal is not an absence.
  if (!fin && !q.error) return null;

  if (!fin) {
    return (
      <Panel name="year-to-date" title="Payroll and billing, year to date">
        <Notice tone="stop" data-panel-error="year-to-date">
          {describeError(q.error, 'Could not load the year-to-date figures.')}
        </Notice>
      </Panel>
    );
  }

  const pay = fin.payroll && typeof fin.payroll === 'object' ? fin.payroll : null;
  const inv = fin.invoices && typeof fin.invoices === 'object' ? fin.invoices : null;
  const tax = fin.tax_notes && typeof fin.tax_notes === 'object' ? fin.tax_notes : null;
  const workers = Array.isArray(pay && pay.worker_breakdown) ? pay.worker_breakdown : null;
  // Is the MONEY half of the payroll block present? The server omits these
  // four fields entirely for a caller without financials.read rather than
  // sending zeros, so their absence is the signal — and a zero year is a real
  // answer that must still render as $0.00, which is why this asks whether the
  // field EXISTS and not whether it is truthy.
  const payMoney = !!pay && pay.total_gross !== undefined && pay.total_gross !== null;
  const hasProfit = fin.profit_estimate !== null && fin.profit_estimate !== undefined;
  const profit = Number(fin.profit_estimate);
  // The sign question above. `> 0` and not `>= 0`: a break-even year estimates
  // no tax either, which is what the server's own se_tax guard says.
  const taxable = hasProfit && Number.isFinite(profit) && profit > 0;

  return (
    <Panel
      name="year-to-date"
      title="Payroll and billing, year to date"
      meta={fin.year ? `${fin.year} so far` : null}
      action={<Link className="of-link" to="/payroll">Payroll</Link>}
    >
      <div className="of-stats">
        {pay ? (
          <section>
            {/*
              The money here is the ACCOUNTANT'S, and a manager is served the
              same payroll object with those four fields absent
              (server/routes/dashboard.js — the owner's "they just can't see
              financials added up or taxes, that's for the accountant").

              So every money Stat is conditional on its own field, and the
              label changes with it. That is not tidiness: `fmtMoney` returns
              an em dash for undefined, so rendering them unconditionally gave
              a manager FOUR DASHES IN A ROW under a heading that says
              "Payroll paid" — which both spends every slot on nothing and
              advertises that something is being withheld. docs/APP_REBUILD.md
              is explicit that a slot with no answer should carry a real fact
              or be dropped.

              Hours and the paystub counts are NOT money and stay for anyone
              with personnel oversight — running crews is what a manager does.
              A manager therefore sees a real, complete row about hours rather
              than a redacted one about money.
            */}
            <SectionLabel>{payMoney ? 'Payroll paid' : 'Payroll hours'}</SectionLabel>
            <div className="of-stats__row">
              {payMoney ? (
                <>
                  <Stat data-ytd="gross" label="Gross" value={fmtMoney(pay.total_gross)} />
                  <Stat data-ytd="deductions" label="Deductions" value={fmtMoney(pay.total_deductions)} />
                  <Stat data-ytd="net" label="Net paid" value={fmtMoney(pay.total_paid)} hint={countHint(pay.paystub_count, 'paystub')} />
                </>
              ) : null}
              <Stat data-ytd="hours" label="Hours paid" value={hoursText(pay.total_hours)} hint={payMoney ? null : countHint(pay.paystub_count, 'paystub')} />
              {payMoney ? (
                <Stat
                  data-ytd="pending"
                  label="Pending"
                  value={fmtMoney(pay.pending_amount)}
                  tone={Number(pay.pending_count) > 0 ? 'warn' : 'neutral'}
                  hint={countHint(pay.pending_count, 'paystub')}
                />
              ) : (
                <Stat
                  data-ytd="pending-count"
                  label="Awaiting approval"
                  value={pay.pending_count == null ? '0' : String(pay.pending_count)}
                  tone={Number(pay.pending_count) > 0 ? 'warn' : 'neutral'}
                />
              )}
            </div>
          </section>
        ) : null}

        {inv ? (
          <section>
            <SectionLabel>Invoiced</SectionLabel>
            <div className="of-stats__row">
              <Stat data-ytd="billed" label="Billed" value={fmtMoney(inv.total_billed)} hint={countHint(inv.count, 'invoice')} />
              <Stat data-ytd="invoices-paid" label="Paid" value={fmtMoney(inv.total_paid)} tone="ok" />
              <Stat data-ytd="invoices-pending" label="Sent, unpaid" value={fmtMoney(inv.total_pending)} />
              {hasProfit ? (
                <Stat
                  data-ytd="profit"
                  label="Profit estimate"
                  value={fmtMoney(fin.profit_estimate)}
                  tone={taxable ? 'ok' : 'warn'}
                  hint="invoices paid less payroll paid"
                />
              ) : null}
            </div>
          </section>
        ) : (
          <Notice tone="info" data-ytd-withheld="invoices">
            Invoicing, profit and tax need the financials permission, so they are not shown here for you. The payroll figures above are complete.
          </Notice>
        )}

        {tax && taxable ? (
          <section>
            <SectionLabel>Tax estimate</SectionLabel>
            <div className="of-stats__row">
              <Stat data-ytd="income-tax" label="Income tax" value={fmtMoney(tax.estimated_income_tax)} />
              <Stat data-ytd="se-tax" label="Self-employment tax" value={fmtMoney(tax.estimated_se_tax)} />
            </div>
            {tax.note ? <p className="of-muted">{tax.note}</p> : null}
          </section>
        ) : null}
        {/*
          `data-ytd-note`, NOT `data-ytd-withheld`. The two withheld notices
          above mean "your permissions did not include this"; this one means
          "the figure does not apply to this year", which is a statement about
          the business rather than about the reader. A browser run caught them
          sharing an attribute — an admin, who is withheld nothing, tripped an
          assertion that no withheld notice was on their screen.
        */}
        {tax && !taxable ? (
          <p className="of-muted" data-ytd-note="no-profit">
            No tax is estimated while the year is not showing a profit.
          </p>
        ) : null}

        <section>
          <SectionLabel>Paid, by worker</SectionLabel>
          {workers ? (
            <DataTable
              columns={WORKER_COLUMNS}
              rows={workers}
              rowKey={(w, i) => w.name ?? i}
              empty="No paystub has been paid this year."
              caption="Net paid per worker, year to date"
              dense
            />
          ) : (
            <p className="of-muted" data-ytd-withheld="workers">
              The per-worker split needs the sensitive-worker permission, so the server did not send it. The company totals above are complete.
            </p>
          )}
        </section>
      </div>
    </Panel>
  );
}

/** "2 paystubs" / "1 paystub" / null when there is no count to report. */
export function countHint(n, noun) {
  if (n === null || n === undefined || n === '') return undefined;
  const v = Number(n);
  if (!Number.isFinite(v)) return undefined;
  return `${v} ${noun}${v === 1 ? '' : 's'}`;
}

/**
 * One worker's year. `data-ytd-worker` carries the name so a test can address a
 * row without depending on the order the server chose to send them in.
 *
 * As a card: the person is the headline and the two figures are labelled
 * facts. `total_paid` is a payroll figure, so it is never an unlabelled number
 * in a card slot — see the same decision in insight/Reports.js's Hours column.
 */
const WORKER_COLUMNS = [
  {
    key: 'name',
    label: 'Worker',
    role: 'title',
    render: (w) => <span data-ytd-worker={w.name || 'Unknown'}>{w.name || 'Unknown'}</span>,
  },
  { key: 'total_hours', label: 'Hours', align: 'right', role: 'fact', render: (w) => hoursText(w.total_hours) },
  { key: 'total_paid', label: 'Net paid', align: 'right', role: 'fact', render: (w) => fmtMoney(w.total_paid) },
];

/** Both panels plus the one sentence that says whose hours they are. */
export default function OverviewInsight() {
  const scope = insightScope(useRole());
  return (
    <>
      {scope === 'self' ? (
        <Notice tone="info" data-panel-scope="self">
          These two panels count your own time entries. The server widens them to the whole company only for admins and managers.
        </Notice>
      ) : null}
      <div className="ofi-panels">
        <WeeklyHoursPanel />
        <TopJobsPanel />
      </div>
      {/*
        Outside .ofi-panels on purpose: that grid is two roughly equal columns,
        and this block is a stat grid plus a table that needs the full width at
        every viewport. It renders nothing for a role the server sends no
        financials to, so a crew member's Overview is unchanged.
      */}
      <YearToDatePanel />
    </>
  );
}
